import React, { RefObject } from 'react';
import { Grid } from '@mui/material';
import CCReportCostCard from './CCReportCostCard';
import { extractIndividualMetricsForGraph, extractMetricsForGraph, extractMetricsForScatterPlot } from './construction-cost-report.util';
import { ReportTypes } from '../../common/utils/util.type';

interface CostInfoProps {
  report: any;
  divisionId?: string;
  displayProjections: boolean;
  displayGraph?: boolean;
  minBudgetPerGSF: number;
  maxBudgetPerGSF: number;
  avgBudgetPerGSF: number;
  minBudgetPerNSF: number;
  maxBudgetPerNSF: number;
  avgBudgetPerNSF: number;
  minBudgetPerUnit: number;
  maxBudgetPerUnit: number;
  avgBudgetPerUnit: number;
  includeSoftCost: boolean;
  typeOfReport: ReportTypes;
  scatterRefGSF?: RefObject<HTMLDivElement>;
  scatterRefUnit?: RefObject<HTMLDivElement>;
  scatterRefNSF?: RefObject<HTMLDivElement>;
}

const CCReportCostInfo: React.FC<CostInfoProps> = ({
  report,
  divisionId,
  displayGraph = false,
  displayProjections,
  maxBudgetPerGSF,
  avgBudgetPerGSF,
  minBudgetPerGSF,
  minBudgetPerNSF,
  maxBudgetPerNSF,
  avgBudgetPerNSF,
  minBudgetPerUnit,
  maxBudgetPerUnit,
  avgBudgetPerUnit,
  includeSoftCost,
  typeOfReport,
  scatterRefGSF,
  scatterRefUnit,
  scatterRefNSF,
}) => {
  const graphData = extractIndividualMetricsForGraph(report?.comparableProjects ?? [], includeSoftCost, 4, divisionId);
  const groupedData = extractMetricsForGraph(report?.comparableProjects ?? [], includeSoftCost, divisionId);
  const scatterPlotData = extractMetricsForScatterPlot(report?.comparableProjects ?? [], includeSoftCost, divisionId);

  return (
    <>
      <Grid container spacing={2} p={1}>
        <Grid size={{ xs: 12, sm: 4, lg: 4 }}>
          <CCReportCostCard
            report={report}
            heading={displayProjections ? 'Projected Cost Based on Gross SF' : 'Cost Per Gross SF'}
            grossMultiplier={report.grossBuildingSF}
            displayProjections={displayProjections}
            minValue={minBudgetPerGSF}
            maxValue={maxBudgetPerGSF}
            avgValue={avgBudgetPerGSF}
            graphData={
              displayGraph && typeOfReport === ReportTypes.QCC
                ? { avgByLocation: graphData.avgGSFByLocation, avgByType: graphData.avgGSFByType }
                : undefined
            }
            groupedData={displayGraph && typeOfReport === ReportTypes.QCC ? groupedData.avgGSF : undefined}
            scatterPlotData={displayGraph && typeOfReport === ReportTypes.CC ? scatterPlotData.avgByGrossSF : undefined}
            typeOfReport={typeOfReport}
            ref={scatterRefGSF}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, lg: 4 }}>
          <CCReportCostCard
            report={report}
            heading={displayProjections ? 'Projected Cost Based on Unit' : 'Cost Per Unit'}
            grossMultiplier={report.unitCount}
            displayProjections={displayProjections}
            minValue={minBudgetPerUnit}
            maxValue={maxBudgetPerUnit}
            avgValue={avgBudgetPerUnit}
            graphData={
              displayGraph && typeOfReport === ReportTypes.QCC
                ? { avgByLocation: graphData.avgUnitCountByLocation, avgByType: graphData.avgUnitCountByType }
                : undefined
            }
            groupedData={displayGraph && typeOfReport === ReportTypes.QCC ? groupedData.avgUnitCount : undefined}
            scatterPlotData={displayGraph && typeOfReport === ReportTypes.CC ? scatterPlotData.avgByUnitCount : undefined}
            typeOfReport={typeOfReport}
            ref={scatterRefUnit}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4, lg: 4 }}>
          <CCReportCostCard
            report={report}
            heading={displayProjections ? 'Projected Cost Based on Net SF' : 'Cost Per Net SF'}
            grossMultiplier={report.netRentableSF}
            displayProjections={displayProjections}
            minValue={minBudgetPerNSF}
            maxValue={maxBudgetPerNSF}
            avgValue={avgBudgetPerNSF}
            graphData={
              displayGraph && typeOfReport === ReportTypes.QCC
                ? { avgByLocation: graphData.avgNSFByLocation, avgByType: graphData.avgNSFByType }
                : undefined
            }
            groupedData={displayGraph && typeOfReport === ReportTypes.QCC ? groupedData.avgNSF : undefined}
            scatterPlotData={displayGraph && typeOfReport === ReportTypes.CC ? scatterPlotData.avgByNetSF : undefined}
            typeOfReport={typeOfReport}
            ref={scatterRefNSF}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default CCReportCostInfo;
